<!-- One betting ticket: a tier's multi, on its own white card.

     A quiet card on the grouped ground: no border, no shadow, a 14px corner —
     `.sheet`'s own paper. Hairlines divide the legs inside it. The one thing
     that changes between the four is how much of the ink is the tier's
     colour — the tier name, the dots.

     On Today the whole ticket is one <button> (`interactive`), with a label
     that says everything the ticket does. Its insides are spans, because a
     button may only hold phrasing content; everywhere else it is an <article>
     with real headings and a real list.

     `multi: null` prints a blank ticket: no ink, a header, a line of small
     print. That is Today before the day's odds are in.

     `compact` is Today's printing, and only Today's: eight strategies share
     one screen there, so the chance folds into the returns line, the working
     and the footer come off — the bookmaker, the time and the independence
     caveat are said once at the top of the page instead — and each leg is one
     line rather than two. A void tier collapses further still, to a single
     row: there is no ticket to read, only a tier the board could not fill.
     Everywhere else — History, the ticket screen, Proposed — the ticket
     prints in full, and nothing below is allowed to change that. -->
<template>
  <component
    :is="interactive ? 'button' : 'article'"
    class="ticket"
    :class="{
      'ticket--blank': !multi,
      'ticket--button': interactive,
      'ticket--compact': compact,
      'ticket--feed': feed,
      'ticket--void-line': voidLine,
    }"
    :style="{ '--tier-ink': ink }"
    :type="interactive ? 'button' : undefined"
    :aria-label="interactive ? label : undefined"
    @click="onClick"
  >
    <!-- A void tier on the board: the tier, the mark, the reason, on one row.
         Four of these can land on an evening pad at once, and four full-height
         tickets saying "not enough runners" is four times the same non-answer
         taking up the screen the live sections need. -->
    <component :is="tag('div')" v-if="voidLine" class="void-line">
      <component :is="tag(headingTag)" class="void-tier">{{ title }}</component>
      <span class="void-mark">Void</span>
      <component :is="tag('p')" class="small void-note">{{ voidNote }}</component>
    </component>

    <component :is="tag('header')" v-if="!voidLine" class="head">
      <!-- Whose ticket this is, above its own name. On the timeline the outer
           grouping is the KICK-OFF, so the strategy has nowhere else to be
           said — and it has to be said, or four tickets at 7:20 pm read as one
           strategy's four. An eyebrow rather than a heading: the tier name
           under it is the ticket's title and two titles would fight. -->
      <!-- WHAT KIND OF PAPER THIS IS, before it is read. Today carries racing
           all-ups and sports multis on one roll, and the only thing telling
           them apart was the strategy's name in the smallest type on the
           ticket. A flag and a ball are told apart at arm's length.

           One glyph per SPORT ON THE TICKET, in leg order — a multi across two
           codes shows both, and a five-code ticket shows three and a count.
           The words are unchanged and carry the meaning; the shapes are
           `aria-hidden` and the sr-only line beside them says the same set out
           loud, because a ticket that is not a button has no aria-label to
           carry it. See lib/sport-glyphs.js. -->
      <component :is="tag('p')" v-if="eyebrow" class="ticket-eyebrow">
        <span v-if="glyphs.length" class="eyebrow-glyphs" aria-hidden="true">
          <IonIcon v-for="g in glyphs" :key="g.sport" class="sport-glyph" :icon="g.icon" />
          <span v-if="glyphsOver" class="glyph-more print">+{{ glyphsOver }}</span>
        </span>
        <span v-if="sportsSaid && !interactive" class="sr-only">{{ sportsSaid }}. </span>{{ eyebrow }}
      </component>
      <component :is="tag(headingTag)" class="tier">{{ title }}</component>
      <span v-if="badge" class="badge">{{ badge }}</span>
      <!-- FEED DENSITY PUTS THE MARK ON THE HEAD LINE. At full size a stamp is
           struck across the odds, which needs a ticket's worth of paper under
           it; in the feed the head line is the only line with room to spare, so
           the mark rides at the end of it, small. An explicit `stamp` — the
           paper trail's WON / LOST / VOID — wins over the pad's own two: a
           proposal from Saturday is not "stale", it is settled. -->
      <span v-if="feed && headStamp" class="head-stamp stamp stamp--small" :class="headStampClass">{{ headStamp }}</span>
      <span v-if="multi && !isVoid && !feed" class="count print"><template v-if="dayBadge">{{ dayBadge }} · </template>{{ legCount }}</span>
    </component>

    <template v-if="multi && !voidLine">
      <span class="odds-line">
        <template v-if="!isVoid">
          <span class="odds print" :class="{ faint: showStale }">{{ money(multi.combinedOdds) }}</span>
          <!-- The ticket keeps its own odds. What the same four legs pay NOW
               prints beside them, small, and never in their place. -->
          <span class="returns-line">
            <!-- Compact folds the chance in here rather than drawing the dots
                 under it: the dots are twenty elements and a line of their own
                 to say what five words say at the end of a line already read.
                 The caveat rides with the number — a feed ticket has no footer
                 to put it in, and a chance quoted without it is truer than it
                 is. -->
            <span class="returns">Stake {{ money(multi.stake) }} · Returns {{ money(multi.potentialReturn) }}<template v-if="compact"> · {{ chance }}</template></span>
            <!-- What the one leg is actually worth, beside what it pays. Only
                 on a single taken at a named book, where the whole reason the
                 ticket exists is the gap between those two numbers. -->
            <!-- WHERE THAT FAIR PRICE CAME FROM, in the two words a row of
                 figures has room for on a 320px phone: "· local books",
                 "· exchange". The whole sentence is what a screen reader hears
                 (`fairAria`) and what the Match page prints under its table —
                 said where there is room to say it, rather than shortened
                 everywhere or run off the edge here. -->
            <span v-if="fairLine" class="small-print fair-line" :aria-label="fairAria">
              <span class="fair-figure">{{ fairLine }}</span><span
                v-if="fairSource"
                class="fair-from"
              > · {{ fairSource }}</span>
            </span>
            <!-- WHAT THE MULTI IS WORTH AGAINST WHAT IT PAYS, on every multi
                 on every screen. The margin on a multi compounds — each leg's
                 book takes its cut and the cuts multiply — and until now the
                 only place that was said was the dollars line further down,
                 which compares this ticket to four different bets. This is the
                 same ticket measured against itself: the fair price of these
                 legs with each book's margin stripped, the price actually
                 offered, and the gap. Faint, because it is a fact about the
                 paper rather than a warning about the bet. -->
            <span v-if="fairMultiLine" class="small-print fair-multi">{{ fairMultiLine }}</span>
            <!-- A MULTI THAT CANNOT BEAT ITS SINGLES SAYS SO, under the line
                 that says what it is worth. Two things put it here: the same
                 legs one at a time at this ticket's own book pay more than the
                 ticket does, or every leg is a positive bet on its own while
                 the folded ticket is not — which is what the estimation-error
                 haircut does to a multi whose edges were thin (engine review
                 #10). Same ink as the line above it: a fact, not an alarm. -->
            <span v-if="singlesNote" class="small-print fair-multi">{{ singlesNote }}</span>
            <!-- WHAT THE MODEL IS WORTH ON TOP OF THE MARKET, on the ticket
                 screen only and only where the legs agree on one figure. See
                 `blendNote`: the fitted `b2` is the entire correction a model
                 strategy makes, and it has been on every model leg since E5
                 with no screen reading it. -->
            <span v-if="blendNote" class="small-print fair-multi">{{ blendNote }}</span>
            <span v-if="combined" class="small-print live-combined">{{ combined.word }} <span class="print">{{ money(combined.odds) }}</span></span>
          </span>
        </template>
        <template v-if="!feed">
          <span v-if="isVoid" class="stamp">Void</span>
          <span v-else-if="showStale" class="stamp">Stale</span>
          <!-- The only animation in the app: the PLACED stamp lands once. -->
          <Transition name="stamp-land">
            <span v-if="isPlaced" class="stamp stamp--placed">Placed</span>
          </Transition>
        </template>
      </span>

      <!-- WHAT THE FOLDING COSTS, in dollars. The same legs priced one by one,
           each at the longest price a bookmaker is paying, come to more than the
           multi does; that gap is what the one slip gives up, and it is a fact
           about the ticket, so it prints on it.

           IT IS A BENCHMARK AND THE WORDS SAY SO. "9.0% off best price ·
           benchmark $18.35", not "singles pay": rolling singles is the same
           arithmetic as a multi, the whole gap is shopping, and the figure is
           not collectable — the games overlap, the prices move, and it takes an
           account at every book. The share leads because it is what compares
           two tickets; the dollars follow the stake. The glossary entry
           `bestprices` is the long version, and the sentence a reviewer struck
           out ("same legs as singles at best price") must not come back.

           IT SAYS WHEN ITS PRICES WERE READ once they are over half an hour
           old, and says nothing at all once they are past their TTL: a stale
           best price overstates the gap. See `BENCHMARK_AS_AT_MS`.

           Faint, and NEVER red. A punter reading this has not made a mistake —
           singles are a different bet with a different variance, not a better
           version of this one — and red would be the app scolding somebody for
           taking the ticket it just printed. A ticket whose own book led the
           board on every leg says "At best price on every leg", for the same
           reason: the line says what happened, including nothing.

           Off on a single (one leg one at a time is the same bet) and off the
           moment one leg is missing from today's board — see `lib/margin.js`. -->
      <component :is="tag('p')" v-if="marginText" class="small margin-line">{{ marginText }}</component>

      <!-- A model ticket shows its working. Classic has no model — its
           probability IS the book's price with the margin left on — so the line
           would read "edge 0.0%" on every ticket, which is worse than silence. -->
      <component :is="tag('p')" v-if="showModelLine" class="small model-line">
        Model <span class="print">{{ pct(multi.modelProbability) }}</span>
        · market <span class="print">{{ pct(multi.fairProbability) }}</span>
        · edge <span class="print">{{ signedPct(multi.edge) }}</span>
        · {{ multi.label }}
      </component>

      <ChanceDots v-if="!isVoid && !compact" :probability="multi.combinedProbability" />

      <!-- The honest number: what these prices are worth once each leg's own
           margin is stripped, over the whole ticket. Only where there is no
           model line already saying so — a strategy with an opinion prints
           edge, not this. -->
      <component :is="tag('p')" v-if="showEvLine" class="small">{{ evLine }}</component>

      <component :is="tag('ul')" v-if="!isVoid && legs.length" class="stubs">
        <component
          :is="tag('li')"
          v-for="(leg, i) in legs"
          :key="leg.id"
          class="stub"
          :class="{ started: started(leg), 'stub--settled': !!legMark(leg), 'stub--compact': compact }"
        >
          <span class="sel">
            <!-- HOW THE LEG WENT, where a leg has been settled at all. Only the
                 paper trail's legs carry `result`, so this is silent on every
                 ticket still ahead of the clock — and it is a glyph beside the
                 name rather than a colour, the same mark a carbon copy uses. -->
            <span v-if="legMark(leg)" class="leg-mark" aria-hidden="true">{{ legMark(leg) }}</span>
            <span class="sel-name">{{ leg.selection }}</span>
            <span v-if="legMeta(leg)" class="sel-meta">{{ legMeta(leg) }}</span>
          </span>
          <!-- The price taken, then the price now, then which one "now" is.
               A leg that has not moved prints exactly what it always did. -->
          <span class="prices">
            <span class="price print">{{ price(leg.price) }}<template v-if="shift(i)"><span class="arrow" aria-hidden="true"> → </span>{{ price(shift(i).live) }}</template></span>
            <span v-if="shift(i)" class="small-print live-mark">{{ shift(i).word }}</span>
            <!-- WHICH LEG IS KILLING IT. The ticket screen prints each leg's
                 own fair price beside the price it was taken at, so the gap the
                 multi's line states once can be read leg by leg — and the leg
                 giving away the most is obvious without any arithmetic. Only
                 here: on Today's roll it would be a figure per leg on a ticket
                 that is deliberately two hundred pixels tall. -->
            <span v-if="legFair(leg)" class="small-print leg-fair">{{ legFair(leg) }}</span>
            <!-- AND WHERE THE BENCHMARK CAME FROM, leg by leg: the longest
                 price a bookmaker is paying on this selection, where it is not
                 the book the leg was taken at. Silent when the leg's own book
                 leads the board. Same screen as the fair price above it and for
                 the same reason: the ticket's one line says how much the slip
                 gives up, and these rows say where. -->
            <span v-if="legBest(leg)" class="small-print leg-best">{{ legBest(leg) }}</span>
            <!-- HOW FAR THE PRICE HAS COME SINCE THE BOARD OPENED. Context and
                 nothing else: the opening price is a fact the app now keeps
                 (F1), the move is the one thing the evidence says a punter can
                 be fast against, and no rule in the engine reads it. Printed
                 only where the move is worth a line (`SINCE_OPEN_SHOWN`), so a
                 board that has barely breathed prints nothing. -->
            <span
              v-if="sinceOpen(leg)"
              class="small-print leg-open"
              :aria-label="sinceOpen(leg).aria"
            >{{ sinceOpen(leg).text }}</span>
          </span>
        </component>
      </component>

      <!-- Whelan's honesty. When the maths says one leg, stacking a second one
           on costs more in margin than it buys in price, and a ticket that
           quietly printed a single leg under a "multi" heading would be the app
           hiding its own answer.

           NOT on a single that was ASKED for. A mispriced-odds ticket is one
           leg on purpose — a second leg would spend the edge it was printed
           for — and "a multi would not help" there reads as an apology for the
           strategy working. The book on the multi is the tell: only a single
           from a singles strategy names one. -->

      <component :is="tag('p')" v-if="isSingle && !askedForSingle" class="small">
        One leg today · a multi would not help.
      </component>

      <!-- A tuned Classic that asked for long shots the board could not supply
           says so on the ticket, once, rather than quietly printing favourites. -->
      <component
        :is="tag('p')"
        v-if="!isVoid && Number.isFinite(multi.longShots) && multi.longShotsFound < multi.longShots"
        class="small"
      >
        Only {{ multi.longShotsFound }} of {{ multi.longShots }} long shots on today’s board.
      </component>

      <!-- Where the prices came from, and what the chance quietly assumes. Off
           on a compact ticket, which says the second half on its returns line
           and reads the first off the dateline at the top of Today.

           NOT "Expect this to lose." on an outrageous ticket any more. The
           chance beside the odds already says 1 in 340, which is the fact; the
           sentence after it was the app's opinion of a ticket it had just
           printed, and a number a reader can check beats a scolding. -->
      <component :is="tag('p')" v-if="isVoid || !compact" class="small">
        <template v-if="isVoid">{{ voidNote }}</template>
        <template v-else>
          Prices from {{ multi.bookmaker }} at <span class="print">{{ time(multi.oddsFetchedAt) }}</span>
          <template v-if="legs.length > 1"> · chance treats legs as unrelated</template>
        </template>
      </component>
    </template>

    <component :is="tag('p')" v-else-if="!multi" class="small">{{ blankNote }}</component>
  </component>
</template>

<script setup>
import { computed } from 'vue';
import { IonIcon } from '@ionic/vue';
import ChanceDots from './ChanceDots.vue';
import { glyphLabel, glyphsFor, moreGlyphs } from '../lib/sport-glyphs.js';
import { TIER_LABEL, tierInk, isStale, money, oneIn, chanceLine, price, time, pct, signedPct, NO_VALUE } from '../lib/format.js';
import { NO_LIVE, liveCombined, liveSource, openingPriceFor, priceShift } from '../lib/live-prices.js';
import {
  BENCHMARK_AS_AT_MS, bestLegPrice, marginLine, oneBookOf, sameBookLine, sameBookPaysMore,
} from '../lib/margin.js';
import { fairFrom, fairFromShort } from '../lib/fair-copy.js';
import { CLASSIC } from '../lib/engine/strategies/classic.js';
import { whenParts, legKickoff } from '../lib/when.js';
import { NEW_FOR_MS, legStarted } from '../lib/timeline.js';
import { isRacingSport } from '../lib/engine/teams.js';
// How far the three devigs have to disagree before a leg prints a range instead
// of a fair price. A leaf with no imports of its own, so the threshold is the
// engine's own constant rather than a number retyped on a screen.
import { FAIR_SPREAD_SHOWN } from '../lib/engine/devig.js';
import { DESIGN_LABEL, localDate } from '../lib/tickets.js';

const props = defineProps({
  /** The multi, or null for a blank ticket. */
  multi: { type: Object, default: null },
  legs: { type: Array, default: () => [] },
  /**
   * What the legs are worth NOW: an array of `{ live, source }` aligned with
   * `legs`, as `livePricesFor` returns. Informational only — nothing here
   * rewrites the multi's odds, stake or returns, which are the record.
   *
   * Null (the default) is "this ticket IS live", which is the pad: its prices
   * are already the board's, and printing them twice would say nothing.
   */
  live: { type: Array, default: null },
  /**
   * Today's board, for the one question the ticket cannot answer off its own
   * paper: what the same legs would come to one by one at the best price going
   * (`lib/margin.js`). Null — the default — prints no margin line at all, which
   * is right for every screen that has no board to hand: History reads a record
   * of a day whose prices are gone.
   */
  universe: { type: Object, default: null },
  /** Epoch ms. Drives STALE and "started"; pass the store's ticking clock. */
  now: { type: Number, default: () => Date.now() },
  /** Today: the whole ticket is one button. */
  interactive: { type: Boolean, default: false },
  /**
   * Today's printing: the chance folded into the returns line, no dots, no
   * working, no footer, one line per leg — and a void tier as a single row.
   * Nothing else in the app passes it, and nothing else should: History and
   * the ticket screen are read one ticket at a time, where the working is the
   * reason to be there.
   */
  compact: { type: Boolean, default: false },
  /**
   * How much paper the ticket is allowed. `'default'` is what every screen but
   * the feed gets and is exactly what it always was.
   *
   * `'feed'` is Today's continuous roll, where the complaint was that ONE
   * ticket filled a phone: the head folds to two lines (strategy · tier ·
   * badge · stamp, then the odds with the stake and returns on its own
   * baseline), the day badge goes — the kick-off heading above already says
   * the day — the legs tighten to a row each, and the tear line gets a third
   * of the air it had. Nothing is removed that a punter reads; the working was
   * already off under `compact`.
   */
  density: { type: String, default: 'default' },
  /**
   * A mark to strike on the head line INSTEAD of the pad's own Placed/Stale —
   * "Won", "Lost", "No result": what the paper trail made of a proposal after
   * the fact. Feed density only; empty everywhere else.
   */
  stamp: { type: String, default: '' },
  /** Which ink that mark takes: 'won' · 'pending' · 'unresulted' · 'placed'. */
  stampTone: { type: String, default: '' },
  /** ISO. When the feed dealt this ticket; NEW for two hours after, unless placed. */
  dealtAt: { type: String, default: '' },
  /** Ticket screen: each stub also names the match and its start time. */
  detailed: { type: Boolean, default: false },
  /** The tier name's element when not interactive. */
  headingTag: { type: String, default: 'h2' },
  /**
   * A short mark beside the tier name — "#1 value" on the board. The caller
   * decides when a ticket has earned one; the ticket only prints it, and an
   * empty string prints nothing. Short by contract: it shares a flex row with
   * the tier name and the leg count.
   */
  badge: { type: String, default: '' },
  /**
   * Whose ticket this is, printed above the tier name. Today's timeline groups
   * by kick-off rather than by strategy, so this is where the strategy's name
   * gets said; everywhere else the section around the ticket already says it
   * and this stays empty.
   */
  eyebrow: { type: String, default: '' },
  blankTitle: { type: String, default: 'No markets open yet' },
  blankNote: { type: String, default: "Tickets drop when today's odds are in." },
});

const emit = defineEmits(['open']);

// Inside a <button>, every element is a span.
const tag = (name) => (props.interactive ? 'span' : name);

// The sports on this ticket, as shapes: one per sport in leg order, three at
// most, and a count for whatever did not fit. Only ever beside the eyebrow —
// where there is no eyebrow the section around the ticket has already said
// whose it is, and a glyph on its own would be a picture with nothing to label.
const glyphs = computed(() => glyphsFor(props.legs));
const glyphsOver = computed(() => moreGlyphs(props.legs));
/** "Horses and NRL" — every sport, including the ones the cap dropped. */
const sportsSaid = computed(() => glyphLabel(props.legs));

const feed = computed(() => props.density === 'feed');

const ink = computed(() => (props.multi ? tierInk(props.multi.tier) : 'var(--rule)'));
// THE RISK, WHERE THERE IS A CHANCE TO READ IT FROM; the design where there is
// not. Every printed ticket now carries a tier worked out from what its legs
// multiply out to (`tierFor`, lib/tier.js) — but a VOID design has no legs and
// therefore no chance, so it prints the way it was cut instead: "Two legs ·
// Void · Not enough favourites on today's board for 2 legs". A ticket with
// neither — a hand-logged bet — keeps the plain word.
const title = computed(() => {
  const m = props.multi;
  if (!m) return props.blankTitle;
  return TIER_LABEL[m.tier] ?? DESIGN_LABEL[m.design] ?? 'Ticket';
});

// The marks a settled leg carries — the same three `ProposedStub` prints, so
// the trail reads identically whether it is met in History or on the feed.
const LEG_MARK = { won: '✓', lost: '✗', void: NO_VALUE };
const legMark = (leg) => LEG_MARK[leg?.result] ?? '';
const isVoid = computed(() => props.multi?.status === 'void');
const isPlaced = computed(() => props.multi?.status === 'placed');

// A void tier has no odds, no legs and no working — only a tier name and a
// reason — so on Today it is a row rather than a ticket. Kept as its own
// computed because four of them decide the shape of the whole ticket.
const voidLine = computed(() => props.compact && isVoid.value);
const voidNote = computed(() =>
  props.multi?.note ?? 'This ticket is void: not enough runners at this tier today.');
// Once it is placed, the prices it was placed at are the point, not their age.
const showStale = computed(() => !isPlaced.value && isStale(props.multi?.oddsFetchedAt, props.now));

/**
 * The one mark the feed's head line carries. A caller-supplied stamp is the
 * record of what HAPPENED and outranks the pad's two, which are both statements
 * about an offer that is still open: a proposal from Saturday has no live price
 * to be stale against.
 */
// Just dealt: two hours from `dealtAt`, on a ticket nobody has placed. It
// outranks STALE for that window — a refill is new AND on the morning's
// prices, and "new" is the fact that moves; the provenance line under the
// ticket still says how old the prices are.
const isNew = computed(() => {
  if (isPlaced.value || isVoid.value) return false;
  const at = Date.parse(props.dealtAt || '');
  return Number.isFinite(at) && props.now - at >= 0 && props.now - at < NEW_FOR_MS;
});

const headStamp = computed(() => {
  if (props.stamp) return props.stamp;
  if (isVoid.value) return 'Void';
  if (isPlaced.value) return 'Placed';
  if (isNew.value) return 'New';
  return showStale.value ? 'Stale' : '';
});

const headStampClass = computed(() => {
  const tone = props.stamp ? props.stampTone : (isPlaced.value ? 'placed' : (isNew.value ? 'new' : ''));
  return tone ? `stamp--${tone}` : '';
});

// One definition of "started" — `legStarted` in lib/timeline.js, which is the
// scheduled start OR the card's own word for a race that has gone
// (`inplayAt` / `marketClosedAt`, whichever comes first). Reused everywhere a
// leg's kickoff matters: this file's own strikethrough AND the `sel-meta` line,
// which is what a screen reader gets instead of the line through the name.
//
// IT IS THE SAME MOMENT `ticketState` EXPIRES THE TICKET AT, and it has to be:
// with the live buffer gone a multi stays on the feed with its first leg under
// way, and the row saying "started" while the ticket above it still offered a
// Place button would be the app arguing with itself.
const parts = (leg) => whenParts(legKickoff(leg), props.now);
const started = (leg) => legStarted(leg, props.now);
/** "AFL · 7:20 pm", "MLB · Tomorrow 9:21 am", "AFL · started" — '' when there is no kickoff to say. */
const whenText = (leg) => (started(leg) ? 'started' : parts(leg).text);

/**
 * THE SAME CLOCK, SAID AS A RACE SAYS IT: "Jumps 7:42 pm".
 *
 * A bare time beside a fixture is a kick-off and needs no word. Beside a runner
 * it was the one number on the line with nothing to say what it meant — the
 * race number, the barrier and the saddlecloth are all numbers too — and a race
 * is the one market in the app that a punter times to the minute. One word
 * carries it. A race that has gone keeps "started", which is what the
 * strikethrough beside it already says.
 */
const jumpText = (leg) => {
  const text = whenText(leg);
  return !text || started(leg) ? text : `Jumps ${text}`;
};

/**
 * The line under a selection, JOINED rather than concatenated.
 *
 * The parts were written inline with their own separators, which printed
 * "Hawthorn to win · · started" the moment one of them was missing — and one of
 * them is missing on every leg of the paper trail, whose legs are stored
 * without a sport title.
 */
const legMeta = (leg) => (isRacingSport(leg?.sport)
  // A RACE SAYS WHERE IT IS RUNNING, ALWAYS, and does not say its code twice.
  // "Winx (4) to win · Randwick R5 · 2:40 pm" is the whole leg: the venue and
  // the race number are what a punter finds the market by, and "Horses" beside
  // a horse's name and a saddlecloth is a word doing no work. The code is still
  // on the leg as `sportTitle` — History's `legsLine` and the sport codes read
  // it — it is only the ticket that leaves it out.
  ? [leg?.match, jumpText(leg)]
  : [leg?.sportTitle, props.detailed && leg?.match, whenText(leg)]
).filter(Boolean).join(' · ');

// THE TICKET-LEVEL DAY, printed once beside the leg count rather than on every
// leg, when every leg agrees on it. Compared as calendar-day strings — see
// `lib/tickets.js`'s own `localDate`, not by adding 86,400,000ms — and only
// when that day is not today's: "today" is what a bare time already says.
const dayBadge = computed(() => {
  const list = props.legs;
  if (!list.length) return '';
  const kickoffs = list.map((leg) => legKickoff(leg));
  if (kickoffs.some((k) => !k)) return '';
  const days = kickoffs.map((k) => localDate(k));
  if (new Set(days).size !== 1) return '';
  const today = localDate(props.now);
  if (days[0] === today) return '';
  // Tomorrow reads as the word; anything further out reads as its when-label's
  // day part, which is already the right weekday-and-date shape.
  const p = whenParts(kickoffs[0], props.now);
  return p.state === 'tomorrow' ? 'Tomorrow' : p.day || '';
});

// "1 leg", not "1 legs": the whole point of the Whelan rule is that one is a
// number this app reaches often enough for the plural to look like a bug.
const legCount = computed(() => `${props.legs.length} leg${props.legs.length === 1 ? '' : 's'}`);
const isSingle = computed(() => !isVoid.value && props.legs.length === 1);

/**
 * The chance, with the one thing it assumes — the sentence the Terms make,
 * short enough to ride at the end of the returns line.
 *
 * A SINGLE gets the chance on its own. There is nothing for one leg to be
 * unrelated to, and a caveat about a thing the ticket does not do reads as the
 * app hedging a number it is confident in.
 */
const chance = computed(() => (props.legs.length > 1
  ? chanceLine(props.multi?.combinedProbability)
  : oneIn(props.multi?.combinedProbability)));

/**
 * A single the STRATEGY asked for, as against a multi that came up one leg
 * short. A named book is most of the answer — a ticket that carries one was
 * placed at one counter — and the rule that named it is the rest: since engine
 * review #4 a multi names its book too, so `best-book` is excluded by hand.
 */
const askedForSingle = computed(() => isSingle.value
  && !!props.multi?.bookmakerKey
  // A `best-book` ticket names its book too (engine review #4), so the key
  // alone no longer separates "one leg on purpose" from "Whelan's answer was
  // one leg". The rule that printed it is the tell.
  && props.multi?.bookmakerRule !== 'best-book');

/**
 * "Fair 2.00 · +20.0%" — what the one leg is worth, beside what it pays.
 *
 * WHICH "FAIR". A leg carries two probabilities and they are not the same
 * thing: `fairProbability` is the BET BOOK's own price with its margin taken
 * off, and `modelProbability` is the market — for a market-consensus strategy,
 * every other book on the game averaged, the bet book excluded from the vote.
 * The market one is printed, because the claim this ticket makes is "this book
 * is out of line with the others", and the book's own devigged price cannot
 * say that about itself: measured against it, every price is fair.
 *
 * Only on a single the strategy asked for; a whelan single at the best price
 * on the board is not this ticket and does not print this line.
 */
const fairLine = computed(() => {
  if (!askedForSingle.value) return '';
  const leg = props.legs[0];
  const market = leg?.modelProbability;
  if (!Number.isFinite(market) || market <= 0 || !Number.isFinite(leg?.price)) return '';
  return `Fair ${price(1 / market)} · ${signedPct(leg.price * market - 1)}`;
});

/**
 * WHERE THAT FAIR PRICE CAME FROM (F1b), where the leg says: a sharp reference
 * priced the side and the fair price is that price alone, net of commission, or
 * it is the local board, which is five books buying much the same feed and
 * should not be read as five opinions.
 *
 * Two words on the paper and the whole sentence in the label. Silent on a leg
 * printed before the app knew the difference, which is every stored leg.
 */
const fairSource = computed(() => {
  const leg = props.legs[0];
  return askedForSingle.value ? fairFromShort(leg?.fairMode, leg?.fairSource) : '';
});

const fairAria = computed(() => {
  const leg = props.legs[0];
  const said = fairFrom(leg?.fairMode, leg?.fairSource);
  return said ? `${fairLine.value} · ${said}` : fairLine.value;
});

/**
 * "Fair $4.40 · pays $4.12 (−6.4%)" — what these legs are worth against what
 * this ticket offers, on every MULTI.
 *
 * WHICH "FAIR", and it is one yardstick for the whole ticket: `fairProbability`
 * is each leg's own price with its own book's margin stripped, multiplied out.
 * Classic's `combinedProbability` is the IMPLIED product with the margin still
 * in it, which is why the two are different numbers and why this line cannot be
 * read off the odds on the paper. `runClassic` computes the devigged product
 * from `bookFair` per leg; every other printer carries it already.
 *
 * The percentage is the multi's own margin, and it COMPOUNDS — it is exactly
 * the product of each leg's gap, which is what the per-leg figures on the
 * ticket screen (`legFair`) let a reader check.
 *
 * Off on a single: `fairLine` is the single's version of this line and says
 * something sharper, and a ticket printing both would print two answers.
 */
const multiFair = computed(() => {
  const m = props.multi;
  if (!m || isVoid.value || props.legs.length < 2) return null;
  const p = m.fairProbability;
  const pays = m.combinedOdds;
  if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(pays) || pays <= 1) return null;
  return { fair: 1 / p, pays, gap: pays * p - 1 };
});

const fairMultiLine = computed(() => {
  const f = multiFair.value;
  return f ? `Fair ${money(f.fair)} · pays ${money(f.pays)} (${signedPct(f.gap)})` : '';
});

/**
 * "fair 2.01 · −8.0%" — one leg's own fair price against the price taken.
 *
 * The same yardstick as the line above, leg by leg, so the two cannot disagree:
 * the leg's own book's price with its own margin off. Only on the ticket screen
 * (`detailed`), and only where the leg carries the figure — a hand-logged bet
 * and a leg off a board that has gone both answer nothing rather than a guess.
 *
 * "FAIR 2.90 TO 3.40" WHERE THE METHODS DISAGREE (engine review #3). Stripping
 * a margin is a modelling choice and the three methods tell three stories about
 * where a long shot's share of it sits; on the tail of a big field they are tens
 * of per cent apart. Where the band is wider than `FAIR_SPREAD_SHOWN` the leg
 * prints the band, because one number there would be the app picking a method
 * and not saying which. The percentage goes with it: a single gap measured off
 * one end of a range is the same claim in different clothes.
 */
function legFair(leg) {
  if (!props.detailed) return '';
  const p = leg?.fairProbability ?? leg?.pFair;
  if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(leg?.price)) return '';
  const { fairProbabilityLow: low, fairProbabilityHigh: high, fairSpread: spread } = leg ?? {};
  if (Number.isFinite(spread) && spread > FAIR_SPREAD_SHOWN && low > 0 && high > 0) {
    return `fair ${price(1 / high)} to ${price(1 / low)}`;
  }
  return `fair ${price(1 / p)} · ${signedPct(leg.price * p - 1)}`;
}

/**
 * How far a price has to have moved since the open before the ticket says so.
 *
 * Two per cent, which is about where a move stops being the board rounding and
 * starts being the market changing its mind. Under it the line is noise on a
 * screen that already carries a fair price, a best price and a live price per
 * leg; over it, it is the one thing the evidence says a punter can be fast
 * against.
 */
const SINCE_OPEN_SHOWN = 0.02;

/**
 * "since open +4.2%" — WHERE THIS PRICE HAS COME FROM, on the ticket screen.
 *
 * CONTEXT, NEVER A TIP, and carbon-faint like the two lines above it. The app
 * keeps every reading of the board it buys (F1), so the opening price is a fact
 * about the day rather than an estimate; what it is NOT is a reason to bet, and
 * no strategy, stake or gate reads it. A shortening price prints a minus and
 * says the same thing the other way round.
 *
 * The percentage is against the leg's own book on its own side — the price this
 * punter could actually have taken at the open.
 */
function sinceOpen(leg) {
  if (!props.detailed || !props.universe) return null;
  const open = openingPriceFor(leg, props.universe);
  if (!open || Math.abs(open.move) < SINCE_OPEN_SHOWN) return null;
  const size = `${(Math.abs(open.move) * 100).toFixed(1)}%`;
  return {
    text: `since open ${signedPct(open.move)}`,
    aria: `${open.move > 0 ? 'up' : 'down'} ${size} since the opening price`,
  };
}

/**
 * "best 1.52 at Neds" — WHICH LEG THE BENCHMARK CAME FROM, on the ticket screen.
 *
 * The margin line above states one share and one figure for the whole slip;
 * this is that figure unpicked, so a punter can see the leg that is giving the
 * most away and the counter that is paying more for it. Nothing where the leg's
 * own book is top of the board, which is the same silence as the line's "At
 * best price on every leg": there is no better price to name.
 *
 * Only on the ticket screen (`detailed`), like `legFair` beside it. Today's
 * roll prints the one line and stops: a book name per leg on a feed ticket is
 * four more things to read before the next start time.
 */
function legBest(leg) {
  if (!props.detailed || !props.universe) return '';
  const best = bestLegPrice(leg, props.universe);
  return best ? `best ${price(best.price)} at ${best.bookmakerName}` : '';
}

/**
 * "Singles at Sportsbet pay more for the same legs" — the multi that cannot
 * beat its own legs, saying so.
 *
 * TWO WAYS A TICKET GETS HERE, and they are different facts.
 *
 * In DOLLARS: the same legs rolled through one at a time at this ticket's own
 * book return more than the ticket does (`sameBookLine`). The compounded margin
 * explains none of that gap — both sides carry the same books' margins — so
 * anything past the rounding on a combined price is the board having moved away
 * from the prices on the paper.
 *
 * In EXPECTATION: every leg is a bet worth having on its own (`price × the
 * market's chance` is above 1 on each) while the ticket they make is not. That
 * is not arithmetic anybody can do on the odds — folded together, the legs'
 * EVs multiply and would stay positive — it is the estimation-error haircut
 * (engine review #10) taking more out of the ticket than any one leg gives up.
 * A punter is owed that in a sentence rather than in a minus sign.
 *
 * It NAMES A BOOK, so it is only printed where there is one book to name: the
 * ticket's own, or the one every leg agrees on. "Best price" is not a counter.
 */
const sameBook = computed(() => (isVoid.value
  ? null
  : sameBookLine(props.multi, props.legs, props.universe)));

const singlesNote = computed(() => {
  const m = props.multi;
  if (!m || isVoid.value || props.legs.length < 2) return '';
  const name = oneBookOf(m, props.legs) ? m.bookmaker : '';
  if (!name || name === 'Best price') return '';
  const eachLegPays = props.legs.every((leg) => {
    const p = leg?.modelProbability;
    return Number.isFinite(p) && Number.isFinite(leg?.price) && leg.price * p - 1 > 0;
  });
  const foldedDoesNot = Number.isFinite(m.ev) && m.ev < 0;
  if (!sameBookPaysMore(sameBook.value) && !(eachLegPays && foldedDoesNot)) return '';
  return `Singles at ${name} pay more for the same legs`;
});

/**
 * "Model corrects the market by b2 = 0.15" — the one number a model ticket is
 * actually built on, said out loud.
 *
 * A model strategy does not print its own opinion: it prints the blend a
 * regression fitted, `sigmoid(b0 + b1·logit(pMarket) + b2·logit(pRaw))`, where
 * `b2` is the whole of what the model adds to the market's price and is small
 * on purpose (engine review #12). The engine has carried it on the leg as
 * `blendWeight` since E5 and nothing on a screen read it, so the ticket screen
 * reads it here.
 *
 * ONLY WHERE THERE IS ONE FIGURE TO PRINT. A fit belongs to a model AND a
 * sport, so a ticket spanning two sports can carry two weights — and one of
 * them printed as "the" correction would be the app naming a number that half
 * its legs were not priced by. Legs carrying no weight say nothing: their
 * probability IS a market price and there is nothing to correct.
 */
const blendNote = computed(() => {
  if (!props.detailed || isVoid.value) return '';
  const weights = new Set(props.legs.map((l) => l?.blendWeight).filter((w) => Number.isFinite(w)));
  if (weights.size !== 1) return '';
  return `Model corrects the market by b2 = ${[...weights][0].toFixed(2)}`;
});

/**
 * The benchmark: what the same legs would come to one by one, each at the best
 * price on the board — `null` on a single, on a void tier, and on any ticket
 * whose legs are no longer all on the board the caller handed down.
 */
const margin = computed(() => (isVoid.value
  ? null
  : marginLine(props.multi, props.legs, props.universe, props.now)));

/**
 * WHEN THE BENCHMARK'S OWN PRICES WERE READ, once they are old enough to matter
 * — "9:11 am", else ''. Under half an hour the figure is this morning's board
 * and needs no dateline; past its TTL there is no figure at all, which
 * `marginLine` decides rather than this line.
 */
const marginAsAt = computed(() => {
  const at = margin.value?.pricedAt;
  if (!Number.isFinite(at) || props.now - at <= BENCHMARK_AS_AT_MS) return '';
  return time(at);
});

/** "9.8% off best price · benchmark $37.80 (−$3.70) · as at 9:11 am". */
const marginText = computed(() => {
  const m = margin.value;
  if (!m) return '';
  const asAt = marginAsAt.value ? ` · as at ${marginAsAt.value}` : '';
  // A ticket whose own book led the board on every leg says so in a clause of
  // its own, with no share and no bracket: there is nothing to compare it to,
  // and "0.0% off (+$0.00)" would be a direction a flat comparison does not
  // have.
  if (m.diff >= 0) return `At best price on every leg${asAt}`;
  return `${pct(m.off)} off best price · benchmark ${money(m.bestPays)} (${money(m.diff)})${asAt}`;
});

// --- original and live --------------------------------------------------------
// `now` is the same bookmaker's price on today's board; `close` is the price
// the match finally closed at. Both are read by `lib/live-prices.js` — this
// component only decides whether the difference is worth a line of ink.

/**
 * One leg's move, or null when there is nothing to say: no live price, or one
 * that rounds to the same cents. `priceShift` is the shared rule — ProposedStub
 * asks the same question of the same legs and used to answer it its own way.
 */
function shift(i) {
  const entry = props.live?.[i];
  return entry ? priceShift(props.legs[i]?.price, entry.live, entry.source) : null;
}

// By identity, so the two helpers in live-prices see exactly the entry that
// belongs to the leg they were handed.
const liveByLeg = computed(() => new Map(props.legs.map((leg, i) => [leg, props.live?.[i] ?? NO_LIVE])));
const resolve = (leg) => liveByLeg.value.get(leg) ?? NO_LIVE;

/** The whole multi at today's prices, when that is a different number. */
const combined = computed(() => {
  if (!props.multi || isVoid.value || !props.legs.length || !props.live?.length) return null;
  const odds = liveCombined(props.legs, resolve);
  const word = liveSource(props.legs, resolve);
  if (odds === null || !word) return null;
  return money(odds) === money(props.multi.combinedOdds) ? null : { odds, word };
});

const showModelLine = computed(() => {
  const m = props.multi;
  return !!m && !isVoid.value && !props.compact && !!m.strategyId && m.strategyId !== CLASSIC.id
    && Number.isFinite(m.modelProbability);
});

// A tier ticket with no model line — Classic itself, or a tier fork of it —
// has nothing else on it to say what the prices are actually worth. `ev` is
// null the moment one leg's own book could not be devigged (see runClassic),
// which is also true of every void ticket, so this line and the model line
// never both print.
//
// `compact` is tested here as well as in `showModelLine` and not inferred from
// it: a compact model ticket has no model line, and a rule that only asked
// "is there a model line?" would answer the question by printing the EV line
// in its place — which is the one thing turning the working off must not do.
const showEvLine = computed(() => {
  const m = props.multi;
  return !!m && !isVoid.value && !props.compact && !!m.tier
    && !showModelLine.value && Number.isFinite(m.ev);
});

/** "67c" under a dollar, "$1.04" at or above it — the expected return on $1. */
function centsOrDollars(value) {
  const returnCents = Math.round(value * 100);
  return returnCents >= 100 ? money(returnCents / 100) : `${returnCents}c`;
}

const evLine = computed(() => {
  const m = props.multi;
  const amount = centsOrDollars(1 + m.ev);
  const clause = m.ev < 0
    ? `the margin, over ${legCount.value}`
    : 'better than fair on this book\'s own prices';
  return `Expected return ${amount} per $1: ${clause}.`;
});

const label = computed(() => {
  const m = props.multi;
  if (!m) return title.value;
  const name = m.label && m.strategyId !== CLASSIC.id
    ? `${m.label}, ${TIER_LABEL[m.tier]} ticket`
    : `${TIER_LABEL[m.tier]} ticket`;
  if (isVoid.value) return `${name}, void`;
  return [
    // The glyphs are ink on the ticket too, and the button's label is the only
    // thing a screen reader is given — so the shapes are said in words, first,
    // where the eye sees them.
    props.eyebrow && sportsSaid.value,
    // The eyebrow is ink on the ticket, so it belongs in the sentence a screen
    // reader gets — on the timeline it is the only thing naming the strategy.
    props.eyebrow,
    name,
    // The badge is ink on the ticket, so it has to be in the sentence a screen
    // reader gets too — the whole ticket is one button and this label is the
    // only thing it says.
    props.badge,
    legCount.value,
    money(m.combinedOdds),
    combined.value && `${combined.value.word} ${money(combined.value.odds)}`,
    oneIn(m.combinedProbability),
    // The caveat is said to a reader who can see the line, so it is said here
    // too — as its own clause rather than through `chanceLine`'s dot, which is
    // punctuation for the eye and a stumble in speech.
    props.legs.length > 1 && 'legs assumed unrelated',
    // The margin is ink on the ticket, so it is in the sentence too — with the
    // dot and the bracket unpicked into clauses, which is how a figure in
    // brackets has to be read aloud to mean anything.
    margin.value && (margin.value.diff >= 0
      ? 'at best price on every leg'
      : `${pct(margin.value.off)} off best price, benchmark ${money(margin.value.bestPays)}`),
    margin.value && margin.value.diff < 0 && `${money(-margin.value.diff)} more`,
    marginAsAt.value && `as at ${marginAsAt.value}`,
    // The fair-versus-pays line is ink on the ticket, so it is in the sentence
    // a screen reader gets too — with the bracket unpicked into a clause.
    multiFair.value && `fair ${money(multiFair.value.fair)}, pays ${money(multiFair.value.pays)}, ${signedPct(multiFair.value.gap)}`,
    // And the sentence under it, where the ticket cannot beat its own legs.
    singlesNote.value,
    props.stamp,
    isPlaced.value && 'placed',
    !props.stamp && showStale.value && 'odds stale',
  ].filter(Boolean).join(', ');
});

function onClick() {
  if (props.interactive && props.multi) emit('open', props.multi.id);
}
</script>

<style scoped>
.ticket {
  display: block;
  width: 100%;
  min-height: var(--tap);
  margin: 0 0 var(--sp-4);
  padding: var(--sp-4);
  border: 0;
  border-radius: var(--r-sheet);
  appearance: none;
  -webkit-appearance: none;
  font: inherit;
  text-align: left;
  color: var(--carbon);
  /* A white card on the grouped ground: `.sheet`'s own paper, drawn here
     rather than composed with the class so the tear-line background this
     used to carry has nowhere to hide. */
  background-color: var(--sheet);
}

.ticket--button {
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

/* Pressed: the paper takes a breath of ink, inside the same frame as the tap. */
.ticket--button:active {
  background-color: color-mix(in srgb, var(--carbon) 5%, transparent);
}

.ticket:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 4px;
}

.head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--sp-3);
}

/* A full-width line of its own above the tier name — `flex-basis: 100%` rather
   than a block outside the header, so the name it labels stays in the same box
   as the badge and the leg count it shares a baseline with. The treatment is
   the badge's, deliberately: both are small tracked labels ABOUT the ticket,
   and inventing a second one would be a third heading size on one ticket. */
.ticket-eyebrow {
  flex: 0 0 100%;
  margin: 0 0 2px;
  font: 700 var(--fs-small)/1.2 var(--font-body);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--carbon-faint);
}

/* THE SHAPES SIT IN THE EYEBROW'S OWN INK AND AT THE EYEBROW'S OWN WEIGHT.
   14px is a touch above the 12px the words are set at, which is what makes a
   ball and a flag legible without making either louder than the strategy's
   name: an outline glyph at 12px is four grey smudges. `--carbon-faint` is the
   minimum ink anything carrying information may take (see DESIGN.md), and it
   is the same ink the word beside it is printed in, so the pair reads as one
   label rather than as a picture and a caption. */
.eyebrow-glyphs {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-right: var(--sp-1);
  /* The row of shapes rides the text's baseline rather than sitting on it —
     an icon's box is square and a capital's is not, so an untranslated glyph
     prints a pixel and a half high. */
  vertical-align: -2px;
}

.sport-glyph {
  width: 14px;
  height: 14px;
  color: var(--carbon-faint);
}

/* "+2": the shapes that did not fit, as a figure. Courier Prime, because it is
   a printed number and every printed number in this app is (`.print` carries
   the face); the tracking comes off because a two-character figure inside a
   tracked label reads as spaced-out type. */
.glyph-more {
  font-size: 11px;
  letter-spacing: 0;
}

.tier {
  display: block;
  margin: 0;
  font: 700 var(--fs-head)/1.1 var(--font-body);
  color: var(--tier-ink);
}

.ticket--blank .tier {
  color: var(--carbon);
}

/* What the ranking made of this ticket, set right beside the tier name rather
   than out at the end — it qualifies the name, it is not a second figure. The
   auto margin is what keeps the leg count at the far edge where it has always
   been. Faint and small: a note about the ticket, not part of its title. */
/* It gives before the row does. The tier name and the leg count are the
   ticket's own facts and never shrink; the badge is a note about it, and at
   320px "Outrageous" beside "+12.3% vs market" is more than the row has. */
.badge {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  margin-right: auto;
  text-overflow: ellipsis;
  font: 700 var(--fs-small)/1 var(--font-body);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--carbon-faint);
  white-space: nowrap;
}

.count {
  font-size: var(--fs-meta);
  line-height: 1;
  color: var(--carbon-faint);
  white-space: nowrap;
}

.odds-line {
  position: relative;
  display: block;
  min-height: var(--fs-odds);
  margin-top: var(--sp-3);
}

.odds {
  display: block;
  font-size: var(--fs-odds);
  font-weight: 700;
  line-height: 1;
  color: var(--carbon);
}

.odds.faint {
  color: var(--carbon-faint);
}

/* The returns and, when there is one, what the same legs pay now: one row, the
   record on the left and the live figure at the far end. With nothing live it
   is a single child at its natural width — the same line it always was. */
.returns-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--sp-3);
  margin-top: var(--sp-1);
}

.returns {
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  font: 400 var(--fs-small)/1.3 var(--font-body);
  color: var(--carbon-faint);
}

/* What the one leg is worth, at the far end of the returns line. Carbon in the
   print face, because the gap between this number and the odds above it is the
   entire reason the ticket printed — a faint one would be a footnote about the
   finding. */
.fair-line {
  /* Its own line under the stake line. Beside it, the stake line wrapped to
     two lines on a phone to make room, and a figure with its meaning split
     over two lines is the one thing the feed head exists to avoid. So the
     FIGURES are what may not break; the source clause after them may, and at
     320px on a long book name it does. */
  font-family: var(--font-print);
  font-variant-numeric: tabular-nums;
  color: var(--carbon);
}

.fair-figure {
  white-space: nowrap;
}

/* Two words, faint, after the figures: the market the fair price was read off.
   It carries its own leading separator so the space before it is a break
   opportunity, which is what lets it drop to a second line instead of running
   past the edge. */
.fair-from {
  color: var(--carbon-faint);
}

/* WHAT THE MULTI IS WORTH AGAINST WHAT IT PAYS. Its own line under the stake
   line — a full-width flex basis, the way `.fair-line` gets one — and faint,
   never red: the gap is the cost of folding four bets into one, not a mistake
   the reader has made. Tabular figures so the two prices line up with the odds
   above them. */
.fair-multi {
  flex: 1 1 100%;
  font-variant-numeric: tabular-nums;
  color: var(--carbon-faint);
  overflow-wrap: anywhere;
}

/* One leg's own fair price, under the price it was taken at and right-aligned
   with it by `.prices`. Faint for the same reason the line above it is. */
.leg-fair {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: var(--carbon-faint);
}

/* And the best price on the board for that leg, under the fair one. Same ink
   and the same column: it is the leg-by-leg half of the benchmark line, not a
   prompt to go and bet somewhere else. A book's name can be long, so this one
   is allowed to wrap where `.leg-fair`'s two figures are not. */
.leg-best {
  font-variant-numeric: tabular-nums;
  color: var(--carbon-faint);
}

/* And how far the price has come since the board opened, under the best one.
   The faintest ink in the column on purpose: it is the only line of the three
   that is context rather than arithmetic about this bet. */
.leg-open {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: var(--carbon-faint);
}

/* The number is the point, so it is carbon in the print face; the word that
   says which number it is stays faint. */
.live-combined {
  flex: none;
  white-space: nowrap;
}

.live-combined .print {
  color: var(--carbon);
}

/* The badge rides the top of the odds line, so a stale price — even a
   four-figure outrageous one — can still be read. VOID has no price to keep
   clear of, so it lands at the start. */
.odds-line .stamp {
  position: absolute;
  top: 0;
  inset-inline-end: 0;
}

.odds-line .stamp:first-child {
  inset-inline-start: 0;
  inset-inline-end: auto;
}

.stubs {
  display: block;
  margin: var(--sp-3) 0 0;
  padding: 0;
  list-style: none;
}

.stub {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-3) 0;
  border-top: 1px solid var(--rule);
}

.sel {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* A LEG IS A ROW, NOT A HEADING. This was body size, which put the selection
   within five pixels of the tier name at the top of the same ticket and made a
   four-leg ticket read as four headings under one. The tier is the ticket's
   hero at 22 and the legs are the list beneath it; the same 14/400 a stub's
   legs have always used is what says so. */
.sel-name {
  font: 400 var(--fs-meta)/1.25 var(--font-body);
}

.sel-meta {
  font: 400 var(--fs-small)/1.35 var(--font-body);
  color: var(--carbon-faint);
}

.stub.started .sel-name {
  text-decoration: line-through;
  color: var(--carbon-faint);
}

/* One column at the end of the stub: the price, and under it the word for what
   the second number is. A leg that has not moved leaves the column holding
   only the price, at exactly the size and place it had before. */
.prices {
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

/* The price sits on the same row as the selection and is read with it, so it
   takes the row's size — not the body size it used to, which made the figure
   the loudest thing on a line whose subject is the leg. */
.price {
  font-size: var(--fs-meta);
  line-height: 1;
  white-space: nowrap;
}

/* The arrow is the only thing between the two numbers, and it is thin on
   purpose: the prices either side of it are what is being read. */
.arrow {
  color: var(--carbon-faint);
}

.live-mark {
  line-height: 1;
}

.small {
  display: block;
  margin: var(--sp-3) 0 0;
  font: 400 var(--fs-small)/1.45 var(--font-body);
  color: var(--carbon-faint);
}

/* Tight under the odds it explains, rather than adrift in the ticket. */
.model-line {
  margin-top: var(--sp-2);
}

/* THE MARGIN IS FAINT, AND IT IS NEVER RED — `.small`'s own --carbon-faint, and
   nothing added. A punter reading it has not made a mistake, and an alarm ink
   would be the app arguing with the ticket above it.
   It sits --sp-1 under the returns rather than `.small`'s --sp-3 because it is
   the second half of that sentence, not a note about the ticket; the feed's own
   --sp-2 still wins there, which is the gap every other line in the roll takes.
   `overflow-wrap` rather than `nowrap`: at 320px the sentence is two lines, and
   two readable lines beat one that runs off the sheet. */
.margin-line {
  margin-top: var(--sp-1);
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}

/* --- compact: Today only ---------------------------------------------------- */
/* Less paper around the print. The perforation along the top edge is what tears
   one ticket from the next, and it goes on doing that at a quarter of the
   margin — the space was there to separate four tickets on a screen, and there
   are eight strategies' worth on this one now. */
/* A card now, like every ticket: the inset is the card's, not the page's. */
.ticket--compact {
  margin-bottom: var(--sp-3);
  padding: var(--sp-3) var(--sp-4);
}

/* One line per leg instead of two. The sport follows the selection inline
   rather than sitting under it — on a board of eight strategies that second
   line was costing more height than it earned, and "· NRL" after the name says
   exactly what it said before. Drawn as a class and a pseudo-element rather
   than a second copy of the markup, so there is one stub to keep right. */
.stub--compact {
  padding: var(--sp-2) 0;
}

/* One line, ellipsised rather than wrapped: the kickoff added to this line
   made "Melbourne Storm to win · NRL · 3:46 pm" long enough to wrap to a
   second line at 320px, which is exactly what "one line per leg" was for —
   two lines here reads as the leg below it shifting up to meet a gap, not as
   more information. The price stays put either way; only the leg's own text
   gives up the room. */
.stub--compact .sel {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.stub--compact .sel-name,
.stub--compact .sel-meta {
  display: inline;
}

.stub--compact .sel-meta::before {
  content: ' · ';
}

/* --- feed: Today's continuous roll ------------------------------------------ */
/* THE COMPLAINT WAS THAT ONE TICKET FILLED A PHONE, and the measurements said
   the same: a two-leg ticket at 390×844 came to most of a screen, of which the
   34px odds figure, the day badge and the air around the tear line were the
   bulk. A feed is read by scrolling THROUGH it — a ticket that fills the
   viewport cannot be compared with the one above it, which is the whole reason
   anybody scrolls a betting board.
   Nothing here invents a type size. Every rule below moves something from one
   role in the table at app.css:72-107 to another — the odds from the 34/700
   hero to the 22/700 row figure, the stake line to meta — or takes air out. */
.ticket--feed {
  /* --sp-3 above the tear and --sp-3 below it. The tear is the join between two
     tickets and it was --sp-5 either side: 40px of paper to say "a new ticket
     starts here", on a screen where the next ticket's own name says it. The
     gap BELOW the legs is the provenance row's own --sp-2 and nothing else:
     the ⓘ under it already carries 44px of tap target, and a margin here on
     top of that printed as a band of blank sheet above a single glyph. */
  margin-bottom: 0;
  padding: var(--sp-3) 0 0;
}

/* ONE LINE, not two. The eyebrow was `flex-basis: 100%` — a line of its own for
   a word — and in the feed it sits on the head line with the tier name it
   labels, which is where a strategy's name reads as a qualifier rather than as
   a heading.
   PACKED LEFT, not spread. `space-between` is right for a row whose two ends
   are two different facts; here the eyebrow ENDS IN A DOT and the tier name
   finishes the phrase, so a Classic ticket with no badge printed "CLASSIC FOUR
   WAYS ·" at one edge and "Low risk" at the other. The stamp takes itself to
   the far end on its own margin. */
.ticket--feed .head {
  /* WRAPS RATHER THAN CLIPS. "Favourites and two long shots" is a strategy's
     whole name, and "FAVOURITES AND TWO LO…" beside a tier is a line nobody
     can read. The eyebrow keeps its words; the tier, the badge and the stamp
     fall to a second line when the first is full. */
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: var(--sp-1) var(--sp-2);
  /* Room at the end of the line for the ⓘ the feed hangs in the corner: its
     44px box overhangs the sheet by --sp-2, so the glyph sits on the text's
     right edge and the stamp stops a clear --sp-3 short of it. */
  padding-right: var(--tap);
}

.ticket--feed .ticket-eyebrow {
  flex: 0 1 auto;
  min-width: 0;
  margin: 0;
  white-space: nowrap;
}

.ticket--feed .ticket-eyebrow::after {
  content: ' ·';
}

/* The record heading's 17/700, not the hero's 22: in a feed the ticket IS a
   row, and the 22 is reserved for the figure underneath it. */
.ticket--feed .tier {
  flex: none;
  font-size: var(--fs-body);
  line-height: 1.2;
  white-space: nowrap;
}

/* It gives before the tier name or the stamp does — see `.badge`. */
.ticket--feed .badge {
  margin-right: 0;
  max-width: none;
  overflow: visible;
  text-overflow: clip;
}

/* A SETTLED leg carries its mark — ✓ ✗ — and the mark is the verdict. Striking
   the selection through as well read every won leg as a loss. The faint ink
   stays: it is still a leg that has been and gone.

   ONLY A SETTLED ONE, and that is the change. The rule used to lift the
   strikethrough off every started leg in the feed, which was harmless while a
   ticket left the feed fifteen minutes BEFORE its first leg ran. With no buffer
   left (`LIVE_BUFFER_MS`) a ticket stays on the roll with a leg under way, and
   the strikethrough is the only thing on the row that says which leg it is. */
.ticket--feed .stub.started.stub--settled .sel-name {
  text-decoration: none;
}

/* The mark at the end of the head line. `margin-left: auto` rather than
   `justify-content`, so a ticket with no stamp leaves the line exactly as it
   was instead of spreading its three parts across the width. */
.ticket--feed .head-stamp {
  flex: none;
  margin-left: auto;
}

/* LINE TWO: the figure and what it means, on one baseline. The odds stop being
   a block — 34px on its own row with the stake line under it was two lines and
   two type sizes for one fact. */
.ticket--feed .odds-line {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 var(--sp-2);
  min-height: 0;
  margin-top: var(--sp-2);
}

.ticket--feed .odds {
  display: inline;
  flex: none;
  font-size: var(--fs-head);
  line-height: 1.2;
}

.ticket--feed .returns-line {
  flex: 1 1 8rem;
  flex-wrap: wrap;
  min-width: 0;
  margin-top: 0;
}

/* The fair line is a flex item of the stake row; a full-width basis is what
   puts it on the line beneath rather than beside. */
.ticket--feed .fair-line {
  flex: 1 1 100%;
}

.ticket--feed .returns {
  font-size: var(--fs-small);
}

/* A leg is a row, and in the feed it is a SHORT one: the hairline still divides
   one from the next, and --sp-1 is enough paper either side of 14/400 text to
   keep the rule off the descenders. */
.ticket--feed .stubs {
  margin-top: var(--sp-2);
}

.ticket--feed .stub {
  padding: var(--sp-1) 0;
}

/* How the leg went, ahead of the name it went for. */
.leg-mark {
  margin-right: var(--sp-1);
  font-weight: 700;
}

/* The notes that survive at this density — "One leg today", a short board's
   long shots — are meta and sit tight under the legs they qualify. */
.ticket--feed .small {
  margin-top: var(--sp-2);
}

/* A void tier as one row. The tier name drops to body size — it is a label on
   a line now, not the heading of something to read — and the mark beside it is
   a small bordered label rather than the big rotated stamp, which needs a
   ticket's worth of paper around it to land as a stamp at all. */
.ticket--void-line {
  min-height: 0;
  margin-bottom: var(--sp-3);
  padding: var(--sp-2) 0;
}

/* A TABLE, not a flow. Four of these stack on a spent board, and laid out as a
   flex row each one started its mark wherever its own tier name happened to
   end — "Low risk" is short, "Medium risk" is not — so the four marks and the
   four reasons sat at four different left edges and the block read as ragged
   text rather than as four rows of the same thing.
   The columns are fixed rather than content-sized, which is what makes them
   line up ACROSS rows: each row is its own grid, so only identical track sizes
   can agree. 7em clears the longest tier name at the body size and scales with
   it, since the reading size is what `em` is relative to. */
.void-line {
  display: grid;
  grid-template-columns: 7em max-content 1fr;
  align-items: baseline;
  gap: var(--sp-1) var(--sp-3);
}

.void-tier {
  margin: 0;
  font: 700 var(--fs-body)/1.3 var(--font-body);
  color: var(--tier-ink);
}

/* The same badge every other status takes: a light tint fill, sentence case,
   no border. */
.void-mark {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--carbon-faint) 12%, white);
  color: var(--carbon-faint);
  font: 600 var(--fs-small)/1.2 var(--font-body);
  letter-spacing: normal;
  text-transform: none;
}

/* Beside the mark when it fits and under it when it does not — a flex basis,
   not a width. The basis is deliberately small: on a phone the reason wraps to
   two lines of small print either way, and wrapping it INSIDE the row costs one
   line where dropping it BELOW the row costs two. `.small`'s top margin is for
   a paragraph in a column; this one is in a row. */
.void-note {
  flex: 1 1 9rem;
  min-width: 0;
  margin: 0;
}

.stamp-land-enter-active {
  transition: opacity 150ms ease-out, transform 150ms ease-out;
}

.stamp-land-enter-from {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
